import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import { CircularProgress, Box } from '@mui/material';
import { lazyPages } from '@config/lazyImports';
import NotFound from '@pages/NotFound';
import { ROUTES } from '@/constants';

// Loading fallback component
const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
    <CircularProgress />
  </Box>
);

/**
 * Pedido do usuário (14/09): "Sugestões" e "Caixa de Sugestões" deixam de ser páginas próprias —
 * viram a aba "Sugestões" de Painel Público (PublicPanelManagement.tsx). Em vez de apagar as
 * rotas antigas (quebraria links salvos/notificações antigas), redireciona pra lá preservando
 * outros parâmetros da URL — em especial `?status=novo` do atalho "Sugestões pendentes" do sino
 * de notificações (useSuggestionsStatusFilterFromUrl.ts), que passa a rodar dentro da aba.
 */
const RedirectToPublicPanelSuggestions: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', 'suggestions');
  return <Navigate to={`${ROUTES.APP_PUBLIC_PANEL}?${params.toString()}`} replace />;
};

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path=""
        element={
          <ProtectedRoute requiredPermission="demands">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.DemandsBoard />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="app"
        element={
          <ProtectedRoute requiredPermission="demands">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.DemandsBoard />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route 
        path="clients" 
        element={
          <ProtectedRoute requiredPermission="clients">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.ClientManagement />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="users" 
        element={
          <ProtectedRoute requiredPermission="users">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.UserManagement />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="roles" 
        element={
          <ProtectedRoute requiredPermission="roles">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.RoleManagement />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="config" 
        element={
          <ProtectedRoute requiredPermission="settings">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.ConfigPage />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="profile" 
        element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.Profile />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="reports" 
        element={
          <ProtectedRoute requiredPermission="reports">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.ReportsPage />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      {/* Pedido do usuário (14/09): "Sugestões" virou a aba "Sugestões" de Painel Público. */}
      <Route path="suggestions" element={<RedirectToPublicPanelSuggestions />} />
      <Route
        path="providers"
        element={
          <ProtectedRoute requiredPermission="providers">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.ProviderManagement />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="checklists"
        element={
          <ProtectedRoute requiredPermission="checklists">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.ChecklistManagement />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="patrimony"
        element={
          <ProtectedRoute requiredPermission="patrimony">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.PatrimonyManagement />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="patrimony/conference/:inspectionId"
        element={
          <ProtectedRoute requiredPermission="patrimony">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.PatrimonyConference />
            </Suspense>
          </ProtectedRoute>
        }
      />
      {/* Pedido do usuário (04/09): QR Codes, Demandas Recorrentes, Caixa de Sugestões e Painel
          Público — recursos do condomínio, antes modais na página de Condomínios, agora páginas
          de 1º nível (mesmo padrão de checklists/patrimony acima). */}
      <Route
        path="qr-codes"
        element={
          <ProtectedRoute requiredPermission="clients">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.QrCodesManagement />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="recurring-demands"
        element={
          <ProtectedRoute requiredPermission="clients">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.RecurringDemandsManagement />
            </Suspense>
          </ProtectedRoute>
        }
      />
      {/* Pedido do usuário (14/09): "Caixa de Sugestões" virou parte da aba "Sugestões" de
          Painel Público. */}
      <Route path="suggestion-box" element={<RedirectToPublicPanelSuggestions />} />
      <Route
        path="public-panel"
        element={
          <ProtectedRoute requiredPermission={['clients', 'suggestions']}>
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.PublicPanelManagement />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="resources"
        element={
          <ProtectedRoute requiredPermission="demands">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.ResourceCenterPage />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="finance" 
        element={
          <ProtectedRoute requiredPermission="finance">
            <Suspense fallback={<LoadingFallback />}>
              <lazyPages.FinancePage />
            </Suspense>
          </ProtectedRoute>
        } 
      />
      {/* Rota interna 404 para caminhos desconhecidos dentro de /app */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <NotFound />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

